import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ethers } from 'ethers';
import PaymentVaultABI from 'abis/PaymentVault';
import RevenueSplitterABI from 'abis/RevenueSplitter';
import { buttonStyles } from '../css/commonStyles';
import {
  cancelBtnSx,
  colors,
  confirmBtnSx,
  dialogTitleSx,
  fieldBlockStyles,
  fieldLabelStyles,
  formFieldStyles,
  modalPaperStyles,
  newRequestBtnSx,
  tabHeaderRowSx,
  tableCardStyles,
  tableContainerStyles,
} from '../css/revenueItems.styles';
import { formatRevenueSharingErrorMessage, isTxUserRejection } from '../utils/revenueSharing.utils';
import { revenueSharingMessage } from '../utils/revenueSharingMessage';
import { ensureCorrectChain } from '../utils/vaultChain.utils';
import { isValidVaultAddress } from '../utils/vaultRoles.utils';

const walletCellSx = {
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  wordBreak: 'break-all',
};

const stepperSx = {
  mb: 3,
  width: 'fit-content',
  maxWidth: '100%',
  '& .MuiStep-root': { flex: '0 0 auto', px: 0.5 },
  '& .MuiStepConnector-root': {
    flex: '0 0 36px',
    maxWidth: 36,
  },
  '& .MuiStepLabel-label': { color: colors.textSecondary, fontFamily: 'Questrial, sans-serif' },
  '& .MuiStepLabel-label.Mui-active': { color: colors.textPrimary },
  '& .MuiStepLabel-label.Mui-completed': { color: colors.textSecondary },
};

const stepStatusCaption = (status) => {
  switch (status) {
    case 'wallet':
      return 'Confirm in wallet…';
    case 'confirming':
      return 'Confirming on-chain…';
    case 'done':
      return 'Done';
    case 'error':
      return 'Failed';
    default:
      return null;
  }
};

const OperatorTeamSection = ({
  vaultAddress,
  chainId,
  splitterAddress,
  operatorRows,
  isVaultAdmin,
  isSplitterAdmin,
  onUpdated,
  rolesLoading = false,
}) => {
  const [newAddress, setNewAddress] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [vaultGrantStatus, setVaultGrantStatus] = useState('idle');
  const [splitterGrantStatus, setSplitterGrantStatus] = useState('idle');

  const effectiveVault = isValidVaultAddress(vaultAddress) ? vaultAddress.trim() : '';

  const existingOperatorAddresses = useMemo(
    () => new Set(operatorRows.map((row) => row.address.toLowerCase())),
    [operatorRows],
  );

  const needsSplitterGrant = !!(splitterAddress && isSplitterAdmin);
  const isAddFlowBusy = busyKey === 'add';

  const grantSteps = useMemo(() => {
    const steps = [{ key: 'vault', label: 'Vault', status: vaultGrantStatus }];
    if (needsSplitterGrant) {
      steps.push({ key: 'splitter', label: 'Revenue Splitter', status: splitterGrantStatus });
    }
    return steps;
  }, [needsSplitterGrant, vaultGrantStatus, splitterGrantStatus]);

  const showGrantStepper =
    isAddFlowBusy || vaultGrantStatus !== 'idle' || splitterGrantStatus !== 'idle';

  const stepperActiveStep = useMemo(() => {
    const inProgressIdx = grantSteps.findIndex(
      (s) => s.status === 'wallet' || s.status === 'confirming' || s.status === 'error',
    );
    if (inProgressIdx >= 0) return inProgressIdx;
    const pendingIdx = grantSteps.findIndex((s) => s.status === 'idle');
    if (pendingIdx >= 0) return pendingIdx;
    return grantSteps.length;
  }, [grantSteps]);

  const showTxError = (err, fallback) => {
    const msg = formatRevenueSharingErrorMessage(err, fallback);
    if (isTxUserRejection(err)) revenueSharingMessage.warning('Transaction was rejected.');
    else revenueSharingMessage.error(msg);
  };

  const grantOnContract = async (contractAddress, abi, account, onStatus) => {
    if (onStatus) onStatus('wallet');
    await ensureCorrectChain(chainId);
    const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
    const signer = provider.getSigner();
    const contract = new ethers.Contract(contractAddress, abi, signer);
    const daoOperatorRole = await contract.DAO_OPERATOR_ROLE();
    const tx = await contract.grantRole(daoOperatorRole, account);
    if (onStatus) onStatus('confirming');
    await tx.wait();
    if (onStatus) onStatus('done');
  };

  const revokeOnContract = async (contractAddress, abi, account) => {
    await ensureCorrectChain(chainId);
    const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
    const signer = provider.getSigner();
    const contract = new ethers.Contract(contractAddress, abi, signer);
    const daoOperatorRole = await contract.DAO_OPERATOR_ROLE();
    const tx = await contract.revokeRole(daoOperatorRole, account);
    await tx.wait();
  };

  const handleAddOperator = async () => {
    const addr = newAddress.trim();
    if (!addr) {
      revenueSharingMessage.error('Enter a wallet address.');
      return;
    }
    if (!/^0x[0-9a-fA-F]{40}$/.test(addr)) {
      revenueSharingMessage.error('Enter a valid wallet address (0x...).');
      return;
    }
    let checksummed;
    try {
      checksummed = ethers.utils.getAddress(addr);
    } catch {
      revenueSharingMessage.error('Enter a valid wallet address (0x...).');
      return;
    }
    if (existingOperatorAddresses.has(checksummed.toLowerCase())) {
      revenueSharingMessage.error('This wallet is already an operator.');
      return;
    }
    if (!isVaultAdmin) {
      revenueSharingMessage.error('Vault ADMIN role is required to grant operators on the Vault.');
      return;
    }
    setBusyKey('add');
    setVaultGrantStatus('idle');
    setSplitterGrantStatus('idle');
    let vaultDone = false;
    try {
      try {
        await grantOnContract(effectiveVault, PaymentVaultABI, checksummed, setVaultGrantStatus);
        vaultDone = true;
      } catch (err) {
        setVaultGrantStatus('error');
        throw err;
      }

      if (needsSplitterGrant) {
        try {
          await grantOnContract(
            splitterAddress,
            RevenueSplitterABI,
            checksummed,
            setSplitterGrantStatus,
          );
          revenueSharingMessage.success('Operator added on Vault and Revenue Splitter.');
        } catch (err) {
          setSplitterGrantStatus('error');
          showTxError(err, 'Vault updated. Failed to grant on Revenue Splitter.');
          if (onUpdated) await onUpdated();
          return;
        }
      } else if (splitterAddress && !isSplitterAdmin) {
        revenueSharingMessage.warning(
          'Operator granted on Vault. Connect a wallet with ADMIN on the Revenue Splitter to complete setup.',
        );
      } else {
        revenueSharingMessage.success('Operator granted on Vault.');
      }

      setNewAddress('');
      setAddModalOpen(false);
      setVaultGrantStatus('idle');
      setSplitterGrantStatus('idle');
      if (onUpdated) await onUpdated();
    } catch (err) {
      if (!vaultDone) showTxError(err, 'Failed to add operator.');
    } finally {
      setBusyKey('');
    }
  };

  const handleOpenAddModal = () => {
    setNewAddress('');
    setVaultGrantStatus('idle');
    setSplitterGrantStatus('idle');
    setAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    if (isAddFlowBusy) return;
    setAddModalOpen(false);
    setNewAddress('');
    setVaultGrantStatus('idle');
    setSplitterGrantStatus('idle');
  };

  const handleGrantVault = async (addr) => {
    if (!isVaultAdmin) return;
    setBusyKey(`vault-grant-${addr}`);
    try {
      await grantOnContract(effectiveVault, PaymentVaultABI, addr);
      revenueSharingMessage.success('Vault operator role granted.');
      if (onUpdated) await onUpdated();
    } catch (err) {
      showTxError(err, 'Failed to grant Vault operator.');
    } finally {
      setBusyKey('');
    }
  };

  const handleGrantSplitter = async (addr) => {
    if (!isSplitterAdmin || !splitterAddress) return;
    setBusyKey(`split-grant-${addr}`);
    try {
      await grantOnContract(splitterAddress, RevenueSplitterABI, addr);
      revenueSharingMessage.success('Revenue Splitter operator role granted.');
      if (onUpdated) await onUpdated();
    } catch (err) {
      showTxError(err, 'Failed to grant Splitter operator.');
    } finally {
      setBusyKey('');
    }
  };

  const handleRevokeAll = async (row) => {
    if (!window.confirm('Remove this operator?')) return;
    setBusyKey(`revoke-${row.address}`);

    const shouldRevokeVault = row.onVault && isVaultAdmin;
    const shouldRevokeSplitter = row.onSplitter && splitterAddress && isSplitterAdmin;
    let vaultRevoked = false;
    let splitterRevoked = false;

    try {
      if (shouldRevokeVault) {
        try {
          await revokeOnContract(effectiveVault, PaymentVaultABI, row.address);
          vaultRevoked = true;
        } catch (err) {
          showTxError(err, 'Failed to revoke Vault operator.');
          return;
        }
      }

      if (shouldRevokeSplitter) {
        try {
          await revokeOnContract(splitterAddress, RevenueSplitterABI, row.address);
          splitterRevoked = true;
        } catch (err) {
          const fallback = vaultRevoked
            ? 'Vault access removed. Failed to revoke Revenue Splitter operator.'
            : 'Failed to revoke Revenue Splitter operator.';
          showTxError(err, fallback);
          if (vaultRevoked && onUpdated) await onUpdated();
          return;
        }
      }

      if (vaultRevoked || splitterRevoked) {
        revenueSharingMessage.success('Operator access updated.');
      }
      if (onUpdated) await onUpdated();
    } finally {
      setBusyKey('');
    }
  };

  if (!isVaultAdmin && !isSplitterAdmin) {
    return null;
  }

  return (
    <Box sx={{ mt: 4, width: '100%' }}>
      <Box sx={tabHeaderRowSx}>
        <Typography
          sx={{
            color: colors.textPrimary,
            fontSize: '1.1rem',
            fontFamily: 'Questrial, sans-serif',
            fontWeight: 400,
          }}
        >
          Operator team
        </Typography>
        {isVaultAdmin && (
          <Button
            variant="contained"
            onClick={handleOpenAddModal}
            disabled={!!busyKey || !effectiveVault || rolesLoading}
            sx={{ ...buttonStyles, ...newRequestBtnSx }}
          >
            Add operator
          </Button>
        )}
      </Box>

      <Box sx={tableCardStyles}>
        <TableContainer component={Box} sx={tableContainerStyles}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Wallet</TableCell>
                <TableCell align="center">Vault</TableCell>
                <TableCell align="center">Revenue Splitter</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rolesLoading && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
                      <CircularProgress size={22} sx={{ color: colors.accent }} />
                      <Typography sx={{ color: colors.textSecondary, fontFamily: 'Questrial, sans-serif' }}>
                        Loading operators…
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!rolesLoading &&
                operatorRows.map((row) => (
                  <TableRow key={row.address} hover>
                    <TableCell>
                      <Typography sx={walletCellSx}>{row.address}</Typography>
                    </TableCell>
                    <TableCell align="center">{row.onVault ? 'Yes' : '—'}</TableCell>
                    <TableCell align="center">{row.onSplitter ? 'Yes' : '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                        {!row.onVault && isVaultAdmin && (
                          <Button
                            size="small"
                            disabled={!!busyKey}
                            onClick={() => handleGrantVault(row.address)}
                            sx={{ textTransform: 'none', color: colors.accent }}
                          >
                            {busyKey === `vault-grant-${row.address}` ? (
                              <CircularProgress size={14} sx={{ color: colors.accent }} />
                            ) : (
                              'Grant Vault'
                            )}
                          </Button>
                        )}
                        {!row.onSplitter && splitterAddress && isSplitterAdmin && (
                          <Button
                            size="small"
                            disabled={!!busyKey}
                            onClick={() => handleGrantSplitter(row.address)}
                            sx={{ textTransform: 'none', color: colors.accent }}
                          >
                            {busyKey === `split-grant-${row.address}` ? (
                              <CircularProgress size={14} sx={{ color: colors.accent }} />
                            ) : (
                              'Grant Splitter'
                            )}
                          </Button>
                        )}
                        {(row.onVault || row.onSplitter) && (isVaultAdmin || isSplitterAdmin) && (
                          <Button
                            size="small"
                            disabled={!!busyKey}
                            onClick={() => handleRevokeAll(row)}
                            sx={{ textTransform: 'none', color: '#ff3b30', minWidth: 72 }}
                          >
                            {busyKey === `revoke-${row.address}` ? (
                              <CircularProgress size={14} sx={{ color: '#ff3b30' }} />
                            ) : (
                              'Remove'
                            )}
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              {!rolesLoading && operatorRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 5, color: colors.textSecondary }}>
                    No operators listed on-chain yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog
        open={addModalOpen}
        onClose={handleCloseAddModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: modalPaperStyles }}
      >
        <DialogTitle sx={dialogTitleSx}>Add operator</DialogTitle>
        <DialogContent>
          {showGrantStepper && (
            <Stepper activeStep={stepperActiveStep} sx={stepperSx}>
              {grantSteps.map((step) => {
                const caption = stepStatusCaption(step.status);
                const isError = step.status === 'error';
                const isComplete = step.status === 'done';
                return (
                  <Step key={step.key} completed={isComplete}>
                    <StepLabel
                      error={isError}
                      optional={
                        caption ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: isError ? '#ff3b30' : colors.textSecondary,
                              fontFamily: 'Questrial, sans-serif',
                            }}
                          >
                            {caption}
                          </Typography>
                        ) : null
                      }
                    >
                      {step.label}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          )}

          {!isAddFlowBusy && needsSplitterGrant && !showGrantStepper && (
            <Typography
              sx={{
                color: colors.textSecondary,
                fontFamily: 'Questrial, sans-serif',
                fontSize: '0.9rem',
                mb: 2,
                lineHeight: 1.5,
              }}
            >
              Granting this operator requires two on-chain transactions (Vault, then Revenue Splitter).
            </Typography>
          )}

          <Box sx={fieldBlockStyles}>
            <Typography sx={fieldLabelStyles}>Wallet address</Typography>
            <TextField
              placeholder="0x..."
              value={newAddress}
              onChange={(e) => setNewAddress(e.target.value)}
              fullWidth
              autoFocus
              disabled={isAddFlowBusy}
              sx={formFieldStyles}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
            <Button onClick={handleCloseAddModal} disabled={isAddFlowBusy} sx={cancelBtnSx}>
              {splitterGrantStatus === 'error' ? 'Close' : 'Cancel'}
            </Button>
            <Button
              variant="contained"
              onClick={handleAddOperator}
              disabled={isAddFlowBusy || !effectiveVault || splitterGrantStatus === 'error'}
              sx={{ ...buttonStyles, ...confirmBtnSx }}
            >
              {isAddFlowBusy ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                'Add operator'
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default OperatorTeamSection;
